import type {
  AdminAccount,
  AdminQueue,
  ApiErrorBody,
  BookQueueResponse,
  CallPurpose,
  HospitalIntegrationStatus,
  HospitalPatientCandidate,
  HospitalRelationship,
  PatientHistory,
  PatientSearchResult,
  PublicConfig,
  QueueStatus,
  QueueTicket,
} from "./types";
import { getSession } from "./auth";
import { env } from "./env";

function functionUrl(name: string, path = "") {
  return `${env.supabaseUrl}/functions/v1/${name}${path}`;
}

async function parseResponse<T>(response: Response): Promise<T> {
  const body = (await response.json().catch(() => ({}))) as T & ApiErrorBody;
  if (!response.ok) {
    throw new Error(body.message || body.error || `เกิดข้อผิดพลาด (${response.status})`);
  }
  return body;
}

function publicHeaders() {
  return {
    apikey: env.publishableKey,
    "Content-Type": "application/json",
  };
}

async function adminHeaders() {
  const session = await getSession();
  if (!session) throw new Error("กรุณาเข้าสู่ระบบอีกครั้ง");
  return {
    ...publicHeaders(),
    Authorization: `Bearer ${session.accessToken}`,
  };
}

export async function getPublicConfig(): Promise<PublicConfig> {
  return parseResponse(
    await fetch(functionUrl("public-config"), { headers: publicHeaders() }),
  );
}

export async function bookQueue(
  idToken: string,
  phoneNumber: string,
): Promise<BookQueueResponse> {
  return parseResponse(
    await fetch(functionUrl("queue-api/book"), {
      method: "POST",
      headers: publicHeaders(),
      body: JSON.stringify({ idToken, phoneNumber }),
    }),
  );
}

export async function getTicket(token: string): Promise<QueueTicket> {
  const result = await parseResponse<{ ticket: QueueTicket }>(
    await fetch(functionUrl("queue-api", `/ticket/${encodeURIComponent(token)}`), {
      headers: publicHeaders(),
    }),
  );
  return result.ticket;
}

export async function getAdminQueues(): Promise<AdminQueue[]> {
  const result = await parseResponse<{ queues: AdminQueue[] }>(
    await fetch(functionUrl("admin-api/queues"), {
      headers: await adminHeaders(),
    }),
  );
  return result.queues;
}

export async function callQueue(
  id: string,
  callPurpose: CallPurpose,
): Promise<AdminQueue> {
  const result = await parseResponse<{ queue: AdminQueue }>(
    await fetch(functionUrl("admin-api", `/queues/${id}/call`), {
      method: "POST",
      headers: await adminHeaders(),
      body: JSON.stringify({ callPurpose }),
    }),
  );
  return result.queue;
}

export async function updateQueueStatus(
  id: string,
  status: Extract<QueueStatus, "COMPLETED" | "CANCELLED" | "NO_SHOW">,
): Promise<void> {
  await parseResponse(
    await fetch(functionUrl("admin-api", `/queues/${id}/status`), {
      method: "POST",
      headers: await adminHeaders(),
      body: JSON.stringify({ status }),
    }),
  );
}

export async function recallQueue(id: string): Promise<void> {
  await parseResponse(
    await fetch(functionUrl("admin-api", `/queues/${id}/recall`), {
      method: "POST",
      headers: await adminHeaders(),
    }),
  );
}

export async function retryNotification(id: string): Promise<void> {
  await parseResponse(
    await fetch(functionUrl("admin-api", `/queues/${id}/retry-notification`), {
      method: "POST",
      headers: await adminHeaders(),
    }),
  );
}

export async function getAdminAccounts(): Promise<{
  accounts: AdminAccount[];
  currentUserId: string;
}> {
  return parseResponse(
    await fetch(functionUrl("admin-api/admin-users"), {
      headers: await adminHeaders(),
    }),
  );
}

export async function createAdminAccount(input: {
  email: string;
  password: string;
  displayName: string;
}): Promise<AdminAccount> {
  const result = await parseResponse<{ account: AdminAccount }>(
    await fetch(functionUrl("admin-api/admin-users"), {
      method: "POST",
      headers: await adminHeaders(),
      body: JSON.stringify(input),
    }),
  );
  return result.account;
}

export async function deleteAdminAccount(userId: string): Promise<void> {
  await parseResponse(
    await fetch(functionUrl("admin-api", `/admin-users/${encodeURIComponent(userId)}`), {
      method: "DELETE",
      headers: await adminHeaders(),
    }),
  );
}

export async function searchPatients(input: {
  query: string;
  fromDate?: string;
  toDate?: string;
}): Promise<PatientSearchResult[]> {
  const parameters = new URLSearchParams({ query: input.query });
  if (input.fromDate) parameters.set("from", input.fromDate);
  if (input.toDate) parameters.set("to", input.toDate);
  const result = await parseResponse<{ patients: PatientSearchResult[] }>(
    await fetch(functionUrl("admin-api", `/patients/search?${parameters}`), {
      headers: await adminHeaders(),
    }),
  );
  return result.patients;
}

export async function getPatientHistory(customerId: string): Promise<PatientHistory> {
  return parseResponse(
    await fetch(
      functionUrl("admin-api", `/patients/${encodeURIComponent(customerId)}/history`),
      { headers: await adminHeaders() },
    ),
  );
}

export async function getHospitalIntegrationStatus(): Promise<HospitalIntegrationStatus> {
  const result = await parseResponse<{ integration: HospitalIntegrationStatus }>(
    await fetch(functionUrl("hospital-api", "/status"), {
      headers: await adminHeaders(),
    }),
  );
  return result.integration;
}

export async function searchHospitalPatients(query: string): Promise<HospitalPatientCandidate[]> {
  const parameters = new URLSearchParams({ query });
  const result = await parseResponse<{ patients: HospitalPatientCandidate[] }>(
    await fetch(functionUrl("hospital-api", `/patients/search?${parameters}`), {
      headers: await adminHeaders(),
    }),
  );
  return result.patients;
}

export async function linkHospitalPatient(input: {
  customerId: string;
  externalPatientId: string;
  relationship: HospitalRelationship;
  isPrimary?: boolean;
}): Promise<void> {
  await parseResponse(
    await fetch(functionUrl("hospital-api", "/patients/link"), {
      method: "POST",
      headers: await adminHeaders(),
      body: JSON.stringify(input),
    }),
  );
}
